import { createClient, SupabaseClient } from "@supabase/supabase-js";
import type { ServiceLayerAuthConfig } from "./config";

// The cached real client, keyed by the (url, key) it was built from. A
// module-level singleton that ignored the config after the FIRST call was a
// cache-poisoning footgun: a stub/wrong-config first call (e.g. a test, or a
// boot order where env vars hadn't resolved) won permanently, and the correct
// config could never replace it — silently returning the wrong client (D-33
// "no silent failures"). We now record the building (url, key) and fail loud on
// any later mismatch instead of handing back a client for a different project.
let cached: { client: SupabaseClient; url: string; key: string } | null = null;

const stubClient = {
  auth: {
    getSession: () => Promise.resolve({ data: { session: null }, error: null }),
    // MUST exist: getFreshServiceLayerAccessToken() calls refreshSession() when
    // getSession() yields no session. An unconfigured stub previously had NO
    // refreshSession, so the call threw `auth.refreshSession is not a function`
    // — and because serviceFetch awaited that throw, EVERY request rejected and
    // the whole app blanked (the 2026-06-01 all-dark regression, #1793). A stub
    // must degrade to "no session", never throw a TypeError (D-33).
    refreshSession: () => Promise.resolve({ data: { session: null, user: null }, error: null }),
    getUser: () => Promise.resolve({ data: { user: null }, error: null }),
    onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
    signInWithOAuth: () => Promise.resolve({ data: { provider: null, url: null }, error: new Error("Supabase not configured") }),
    signOut: () => Promise.resolve({ error: null }),
  },
  from: () => ({ select: () => ({ single: () => Promise.resolve({ data: null, error: null }) }) }),
} as unknown as SupabaseClient;

export function getSupabaseClient(config: ServiceLayerAuthConfig): SupabaseClient {
  const url = (config.supabaseUrl ?? "").trim();
  const key = (config.supabaseAnonKey ?? "").trim();

  // Unconfigured → stub (unchanged). A stub call NEVER populates or poisons the
  // cache, so a later REAL config still builds and wins (first-real-config-wins,
  // not first-call-wins).
  if (!url || !key) {
    return stubClient;
  }

  if (cached) {
    // Cached-hit guard: the requested config MUST match what built the cache.
    // Returning a client for a DIFFERENT (url, key) would silently talk to the
    // wrong Supabase project — unrecoverable cache poisoning. Fail loud (D-33).
    if (cached.url !== url || cached.key !== key) {
      const msg =
        "[service-layer] getSupabaseClient called with a config that differs " +
        "from the one that built the cached client (url/anon-key mismatch). " +
        "Refusing to return a client for a different Supabase project.";
      // eslint-disable-next-line no-console
      console.error(msg, {
        cachedUrl: cached.url,
        requestedUrl: url,
        keyChanged: cached.key !== key,
      });
      throw new Error(msg);
    }
    return cached.client;
  }

  const client = createClient(url, key, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,
      flowType: 'implicit',
      storageKey: 'sb-clanda-sl',
    }
  });
  cached = { client, url, key };
  return client;
}

/**
 * Obtain a non-stale Service-Layer access token from the canonical SL Supabase
 * client (storageKey `sb-clanda-sl`, `autoRefreshToken: true`).
 *
 * The SPA's `serviceFetch` reads the access_token RAW from
 * localStorage[`sb-clanda-sl`] to build the `Authorization: Bearer` fallback
 * header. A raw read NEVER triggers Supabase auto-refresh, so after a
 * long-idle tab an EXPIRED JWT keeps being sent and the Service Layer returns
 * degraded/empty 200s or 403s — surfacing as silent blank pages
 * (BUG-FE-SILENT-SESSION-JWT-DEGRADATION-2026-05-31, the recurring
 * "Steffen-Friday" incident).
 *
 * This routes through the SL client's `auth.getSession()` (which auto-refreshes
 * a token at/near expiry against the `sb-clanda-sl` store) and, if that still
 * yields no session, an explicit `auth.refreshSession()`. Returns the fresh
 * `access_token`, or `null` when no session can be re-established (signed out /
 * refresh token revoked). Callers MUST treat `null` as "session is gone" and
 * signal loudly — never silently degrade.
 */
export async function getFreshServiceLayerAccessToken(
  config: ServiceLayerAuthConfig,
): Promise<string | null> {
  const auth = getSupabaseClient(config).auth;

  // getSession() auto-refreshes when the persisted token is near/over expiry.
  const { data: sessionData } = await auth.getSession();
  const sessionToken = sessionData.session?.access_token;
  if (sessionToken) {
    return sessionToken;
  }

  // No usable session from getSession() — force an explicit refresh attempt.
  const { data: refreshed } = await auth.refreshSession();
  return refreshed.session?.access_token ?? null;
}



